/**
 * UndoToast — sticky time-bounded undo banner.
 * Appears immediately after a successful worker modification with a 30-second
 * countdown. Reverts via /api/command/undo-modify which is already wired.
 */
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowUUpLeft, X, CircleNotch } from '@phosphor-icons/react';

const COUNTDOWN_SECONDS = 30;

export const UndoToast = ({ visible, summary, onUndo, onDismiss }) => {
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [reverting, setReverting] = useState(false);
  const dismissedRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      setSecondsLeft(COUNTDOWN_SECONDS);
      setReverting(false);
      dismissedRef.current = false;
      return undefined;
    }
    setSecondsLeft(COUNTDOWN_SECONDS);
    dismissedRef.current = false;
    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (!dismissedRef.current && onDismiss) onDismiss();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [visible, onDismiss]);

  const handleUndo = async () => {
    if (reverting) return;
    setReverting(true);
    try {
      await onUndo();
    } finally {
      dismissedRef.current = true;
      setReverting(false);
      if (onDismiss) onDismiss();
    }
  };

  const handleDismiss = () => {
    dismissedRef.current = true;
    if (onDismiss) onDismiss();
  };

  // Progress fraction for the countdown ring (0..1)
  const progress = secondsLeft / COUNTDOWN_SECONDS;
  // SVG circle for the countdown
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  const strokeOffset = circumference * (1 - progress);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.96 }}
          transition={{ duration: 0.18 }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 px-4"
          data-testid="undo-toast"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="flex items-center gap-3 bg-[#0E1420] border border-[rgba(0,229,192,0.22)] rounded-full pl-4 pr-1 py-1 shadow-[0_8px_24px_rgba(0,0,0,0.4)]">
            {/* Countdown ring */}
            <div className="relative w-5 h-5 shrink-0">
              <svg className="w-5 h-5 -rotate-90" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
                <circle
                  cx="12"
                  cy="12"
                  r={radius}
                  fill="none"
                  stroke="#00E5C0"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeOffset}
                  className="transition-[stroke-dashoffset] duration-1000 ease-linear"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-[#00E5C0]">
                {secondsLeft}
              </span>
            </div>

            <div className="text-xs text-[#F0F4F8] truncate max-w-[280px]" data-testid="undo-toast-summary">
              {summary || 'Change applied.'}
            </div>

            <button
              onClick={handleUndo}
              disabled={reverting}
              data-testid="undo-toast-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[rgba(0,229,192,0.10)] text-[#00E5C0] text-[11px] font-semibold rounded-full hover:bg-[rgba(0,229,192,0.18)] transition-colors disabled:opacity-50"
            >
              {reverting ? (
                <CircleNotch size={11} className="animate-spin" weight="bold" />
              ) : (
                <ArrowUUpLeft size={11} weight="bold" />
              )}
              Undo
            </button>

            <button
              onClick={handleDismiss}
              data-testid="undo-toast-dismiss"
              className="w-6 h-6 flex items-center justify-center rounded-full text-[#7D8896] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors"
              title="Dismiss"
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UndoToast;
